import React, { useState, useEffect } from "react";
import config from "../../config";
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  TextField,
  Box,
  CircularProgress,
  Grid,
  Button,
} from "@mui/material";
import { motion } from "framer-motion";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import * as XLSX from "xlsx";
import { toast } from 'react-toastify';
const company = JSON.parse(sessionStorage.getItem("companyDetails") || "{}");
const Companycurrency = company?.currency ?? "";
export default function Addonprofit() {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const token = sessionStorage.getItem("token");
  const url = `${config.API_BASE_URL}${config.PRODUCT_LIST_API}`;
  const fetchData = async () => {
    setLoading(true);
    try {
      const fd = fromDate || "null";
      const td = toDate || "null";
      const res = await fetch(
        `${config.API_BASE_URL}/home/GetAddonprofits/${fd}/${td}/${company?.id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const text = await res.text();
      setReport(text ? JSON.parse(text) : null);
    } catch (err) {
      console.error("Error fetching Addon Profit:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  if (loading)
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress size={60} />
      </Box>
    );

  if (!report)
    return (
      <Typography variant="h6" align="center" sx={{ mt: 4 }}>
        No data available
      </Typography>
    );

  const filteredAddons = report.groupTotals?.filter((entry) =>
    entry.name?.toLowerCase().includes(searchText.toLowerCase())
  );

  // 🔹 Export Excel Function
  const exportToExcel = () => {
    if (!filteredAddons || filteredAddons.length === 0) {
      toast.error("No data to export!");
      return;
    }

    const formattedData = filteredAddons.map((entry, index) => ({
      "#": index + 1,
      "Addon Name": entry.name,
      [`Total Price (${Companycurrency})`]: entry.totalPrice?.toFixed(2),
      [`GST Amount (${Companycurrency})`]: entry.gstAmount?.toFixed(2),
      [`Base Price (${Companycurrency})`]: entry.basePriceWithoutGst?.toFixed(2),
    }));

    const worksheet = XLSX.utils.json_to_sheet(formattedData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "AddonProfit");

    XLSX.writeFile(workbook, "AddonProfitReport.xlsx");
  };

  const summaryCards = [
    { label: "Total Addon Price", value: report.totalAddonPrice, color: "#1976d2", bg: "rgba(25,118,210,0.1)" },
    { label: "Total GST Amount", value: report.totalGstAmount, color: "#388e3c", bg: "rgba(56,142,60,0.1)" },
    { label: "Total Base Price (Excl. GST)", value: report.totalBasePriceWithoutGst, color: "#d32f2f", bg: "rgba(211,47,47,0.1)" },
    { label: "Total Addon Types", value: report.groupTotals?.length, color: "#9c27b0", bg: "rgba(156,39,176,0.1)" },
  ];

  return (
    <Box sx={{ p: 4 }}>
      <Typography variant="h4" fontWeight="bold" mb={3} textAlign="center" sx={{ color: "#0a2540" }}>
        ➕ Addon Summary
      </Typography>

      {/* Filters */}
      <Grid container spacing={2} mb={3} alignItems="center">
        <Grid item xs={12} sm={4}>
          <TextField type="date" label="From Date" fullWidth value={fromDate} onChange={(e) => setFromDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <TextField type="date" label="To Date" fullWidth value={toDate} onChange={(e) => setToDate(e.target.value)} InputLabelProps={{ shrink: true }} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <Button variant="contained" fullWidth onClick={fetchData} sx={{ height: "100%", backgroundColor: "#0a2540" }}>
            Apply Filter
          </Button>
        </Grid>
      </Grid>

      {/* Summary Cards */}
      <Grid container spacing={3} mb={4}>
        {summaryCards.map((item, i) => (
          <Grid item xs={12} sm={6} md={3} key={i}>
            <motion.div whileHover={{ scale: 1.07 }}>
              <Paper elevation={4} sx={{ p: 3, borderRadius: 3, textAlign: "center", background: item.bg }}>
                <Typography variant="body2" color="text.secondary">{item.label}</Typography>
                <Typography variant="h5" fontWeight="bold" sx={{ color: item.color }}>
                  {typeof item.value === "number" && item.label !== "Total Addon Types" ? `${Companycurrency}${item.value?.toFixed(2)}` : item.value}
                </Typography>
              </Paper>
            </motion.div>
          </Grid>
        ))}
      </Grid>

      {/* Search + Export */}
      <Box mb={3} display="flex" alignItems="center" gap={2}>
        <TextField label="🔍 Search Addon" size="small" value={searchText} onChange={(e) => setSearchText(e.target.value)} sx={{ width: 300 }} />

        {/* 🔹 Export Button */}
        <Button variant="outlined" startIcon={<FileDownloadIcon />} onClick={exportToExcel} sx={{ height: "40px", borderRadius: 2 }}>
          Export
        </Button>
      </Box>

      {/* Table */}
      <Paper sx={{ borderRadius: 2 }}>
        <TableContainer sx={{ maxHeight: 550 }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell>#</TableCell>
                <TableCell>Addon Name</TableCell>
                <TableCell>Total Price {Companycurrency}</TableCell>
                <TableCell>GST Amount {Companycurrency}</TableCell>
                <TableCell>Base Price {Companycurrency}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredAddons?.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center">No records found</TableCell></TableRow>
              ) : (
                filteredAddons.map((entry, i) => (
                  <TableRow key={i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell>{entry.name}</TableCell>
                    <TableCell>{Companycurrency}{entry.totalPrice?.toFixed(2)}</TableCell>
                    <TableCell>{Companycurrency}{entry.gstAmount?.toFixed(2)}</TableCell>
                    <TableCell>{Companycurrency}{entry.basePriceWithoutGst?.toFixed(2)}</TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
}
